<template>
  <div class="finance-periods-page">
    <div class="page-header">
      <h1>{{ t('Gestão Financeira de Pessoal') }}</h1>
    </div>

    <Tabs v-model:value="activeTab">
      <TabList>
        <Tab :value="0">
          <i class="pi pi-calendar mr-2"></i>
          {{ t('Folha de Pagamento') }}
        </Tab>
        <Tab :value="1">
          <i class="pi pi-wallet mr-2"></i>
          {{ t('Solicitações de Reembolso') }}
        </Tab>
        <Tab :value="2">
          <i class="pi pi-money-bill mr-2"></i>
          {{ t('Solicitações de Adiantamento') }}
        </Tab>
        <Tab :value="3">
          <i class="pi pi-minus-circle mr-2"></i>
          {{ t('Deduções de Colaboradores') }}
        </Tab>
        <Tab :value="4">
          <i class="pi pi-cog mr-2"></i>
          {{ t('Regras de Pagamento') }}
        </Tab>
      </TabList>

      <TabPanels>
        <TabPanel :value="0">
          <TabDescriptor
            icon="pi pi-calendar"
            :title="t('Folha de Pagamento')"
            :description="t('Gerencie os períodos de folha de pagamento, processe pagamentos de funcionários e acompanhe a situação financeira da folha.')"
            :tooltip="t('Um período representa um intervalo de tempo (geralmente mensal) para calcular e pagar salários.')"
            :guidanceSections="payrollGuidanceSections"
          />
          <PayrollPage />
        </TabPanel>
        <TabPanel :value="1">
          <TabDescriptor
            icon="pi pi-wallet"
            :title="t('Solicitações de Reembolso')"
            :description="t('Gerencie as solicitações de reembolso de despesas pagas pelos operadores.')"
            :tooltip="t('Funcionários podem solicitar reembolso de despesas profissionais que pagaram do próprio bolso.')"
            :guidanceSections="refundsGuidanceSections"
          />
          <PayrollRefundsPage />
        </TabPanel>
        <TabPanel :value="2">
          <TabDescriptor
            icon="pi pi-money-bill"
            :title="t('Solicitações de Adiantamento')"
            :description="t('Gerencie as solicitações de adiantamento salarial.')"
            :tooltip="t('Funcionários podem solicitar adiantamento de salário antes do período de pagamento.')"
            :guidanceSections="advancesGuidanceSections"
          />
          <EmployeeAdvancesPage />
        </TabPanel>
        <TabPanel :value="3">
          <TabDescriptor
            icon="pi pi-minus-circle"
            :title="t('Deduções de Colaboradores')"
            :description="t('Gerencie deduções de colaboradores que serão descontadas da folha de pagamento.')"
            :tooltip="t('Crie e gerencie deduções que serão aplicadas automaticamente nos períodos de pagamento.')"
            :guidanceSections="deductionsGuidanceSections"
          />
          <EmployeeDeductionsPage />
        </TabPanel>
        <TabPanel :value="4">
          <TabDescriptor
            icon="pi pi-cog"
            :title="t('Regras de Pagamento')"
            :description="t('Configure as regras de pagamento para cálculo automático da folha de pagamento.')"
            :tooltip="t('As regras definem quanto cada funcionário recebe com base em cargos, comissões e bonificações.')"
            :guidanceSections="paymentRulesGuidanceSections"
          />
          <PaymentRulesPage />
        </TabPanel>
      </TabPanels>
    </Tabs>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

interface GuidanceSection {
  title: string;
  icon: string;
  description: string;
  tips: string[];
}

const { t } = useI18n();
const activeTab = ref<number>(0);

// Accordion-based guidance sections for progressive disclosure
const payrollGuidanceSections: GuidanceSection[] = [
  {
    title: t('Passo a Passo'),
    icon: 'pi pi-directions',
    description: t('O fluxo básico para criar e processar um período de folha:'),
    tips: [
      t('Clique em "Novo Período" para iniciar'),
      t('Selecione funcionário e franquia (eles devem estar vinculados)'),
      t('Defina as datas de início e fim do período'),
      t('Preencha o wizard com os dados necessários'),
      t('Revise e confirme o cálculo na última etapa'),
      t('Use "Desfazer" para reverter períodos não confirmados (status 0-4)')
    ]
  },
  {
    title: t('Regras Críticas'),
    icon: 'pi pi-exclamation-triangle',
    description: t('Evite erros comuns seguindo estas regras:'),
    tips: [
      t('✓ CORRETO: Jan 1-31 → Fev 1-28 → Mar 1-31 (contínuos, sem sobreposição)'),
      t('✗ ERRADO: Jan 1-31 → Fev 15-28 (gap de 15 dias = dias não pagos!)'),
      t('✗ ERRADO: Jan 1-31 → Fev 20-28 (sobreposição = dias pagos duas vezes!)'),
      t('DICA: O sistema calcula automaticamente a data de início baseado no período anterior'),
      t('⚠️ Depois de confirmar (status 4), não tem mais volta!')
    ]
  },
  {
    title: t('Status do Período'),
    icon: 'pi pi-info-circle',
    description: t('O que cada status significa:'),
    tips: [
      t('Aberto (0): Período criado, nada feito ainda'),
      t('Receitas (1): Serviços vinculados ao período'),
      t('Despesas (2): Despesas do período vinculadas'),
      t('Calculado (3): Sistema calculou os pagamentos'),
      t('Confirmado (4): Você revisou e criou as despesas de pagamento'),
      t('Pago (5): Pagamentos processados ✓'),
      t('Arquivado (-1): Registro histórico (não pode mais ser editado)')
    ]
  }
];

const refundsGuidanceSections: GuidanceSection[] = [
  {
    title: t('Como Funciona'),
    icon: 'pi pi-directions',
    description: t('O fluxo de aprovação de reembolsos:'),
    tips: [
      t('Funcionário tira foto do comprovante e cria solicitação'),
      t('Você recebe notificação e clica para revisar'),
      t('Verifique: comprovante válido? valor correto? despesa justificada?'),
      t('Aprove → Valor vai para folha de pagamento do funcionário'),
      t('Rejeite → Funcionário pode corrigir e solicitar novamente')
    ]
  },
  {
    title: t('Checklist de Aprovação'),
    icon: 'pi pi-check-square',
    description: t('Antes de aprovar, confirme:'),
    tips: [
      t('Comprovante está legível? (nome, data, valor visíveis)'),
      t('Valor faz sentido para o tipo de despesa?'),
      t('Despesa é relacionada ao trabalho? (não pode ser pessoal)'),
      t('Funcionário tem permissão para esse tipo de gasto?')
    ]
  },
  {
    title: t('O Que Acontece Depois'),
    icon: 'pi pi-info-circle',
    description: t('Após aprovar o reembolso:'),
    tips: [
      t('Valor é COMMITIDO - aparecerá na folha de pagamento'),
      t('Criada automaticamente uma despesa para a franquia'),
      t('Não pode ser cancelado (apenas rejeitado antes de aprovar)'),
      t('Reembolsos aprovados aparecem na etapa "Adiantamentos e Deduções" do wizard'),
      t('Mantenha comprovantes organizados para auditoria fiscal')
    ]
  }
];

const advancesGuidanceSections: GuidanceSection[] = [
  {
    title: t('O Que São'),
    icon: 'pi pi-question-circle',
    description: t('Funcionário precisa de dinheiro antes do dia do pagamento (ex: emergência médica). Você adianta o valor e desconta do próximo salário.'),
    tips: [
      t('Fluxo de caixa: Você controla quando o dinheiro sai'),
      t('Rastreamento: Mantém histórico de adiantamentos por funcionário'),
      t('Limite: Evita que funcionário peça adiantamento todo mês'),
      t('Matemática clara: Sabe exatamente quanto será descontado')
    ]
  },
  {
    title: t('Como Funciona a Matemática'),
    icon: 'pi pi-calculator',
    description: t('Exemplo prático:'),
    tips: [
      t('Salário normal: R$ 2.000,00'),
      t('Adiantamento solicitado: R$ 500,00'),
      t('─────────────────────────────'),
      t('Salário final: R$ 1.500,00  ⚠️'),
      t('Na próxima folha → Valor é descontado AUTOMATICAMENTE'),
      t('Funcionário recebe: Salário calculado - Adiantamento')
    ]
  },
  {
    title: t('Antes de Aprovar'),
    icon: 'pi pi-exclamation-triangle',
    description: t('Pergunte-se:'),
    tips: [
      t('Funcionário realmente precisa ou é apenas "querer"?'),
      t('Temos fluxo de caixa para este adiantamento agora?'),
      t('Já dei adiantamento este mês? (verifique histórico)'),
      t('O motivo é justificável? (emergências vs. compras não essenciais)'),
      t('Configure limites máximos nas Regras de Pagamento'),
      t('Exija motivo detalhado para aprovação')
    ]
  }
];

const deductionsGuidanceSections: GuidanceSection[] = [
  {
    title: t('O Que São'),
    icon: 'pi pi-question-circle',
    description: t('Deduções são valores que devem ser descontados do salário do colaborador, como empréstimos, materiais quebrados, ou adiantamentos não pagos.'),
    tips: [
      t('Rastreamento: Mantém histórico de deduções por funcionário'),
      t('Flexibilidade: Pode ser parcial (ex: descontar R$ 200 de uma dívida de R$ 500)'),
      t('Controle: Você decide quando descontar e quanto'),
      t('Automático: Deduções aparecem no wizard da folha de pagamento')
    ]
  },
  {
    title: t('Como Funciona'),
    icon: 'pi pi-directions',
    description: t('O fluxo de criação e aplicação de deduções:'),
    tips: [
      t('Crie uma dedução (selecione funcionário, valor, motivo)'),
      t('Status ABERTO: Dedução criada, ainda não descontada'),
      t('Status PARCIAL: Parte do valor já foi descontada'),
      t('Status PAGO: Dedução totalmente descontada'),
      t('No wizard da folha → Selecione as deduções a aplicar'),
      t('Sistema desconta automaticamente do pagamento final')
    ]
  },
  {
    title: t('Antes de Criar'),
    icon: 'pi pi-exclamation-triangle',
    description: t('Boas práticas:'),
    tips: [
      t('Documente tudo: Anexe fotos/comprovantes quando possível'),
      t('Comunique o funcionário: Sempre avise antes de criar uma dedução'),
      t('Valide a dívida: Certifique-se que o valor está correto'),
      t('Seja transparente: Motivo claro e justificável'),
      t('Considere parcelamento: Para valores altos, divida em múltiplas deduções'),
      t('Deduções podem ser canceladas se necessário')
    ]
  }
];

const paymentRulesGuidanceSections: GuidanceSection[] = [
  {
    title: t('Tipos de Regra'),
    icon: 'pi pi-list',
    description: t('Os 3 tipos de regras disponíveis:'),
    tips: [
      t('1️⃣ FIXO POR CARGO (Salário Base): Profissional recebe R$ 1.000,00 fixo por mês, independentemente de quantos serviços fez'),
      t('2️⃣ COMISSÃO POR SERVIÇO (Percentual): Exemplo: 10% de cada serviço realizado. Se fez R$ 5.000 em serviços → comissão = R$ 500'),
      t('3️⃣ BONIFICAÇÃO (Metas e Prêmios): Exemplo: R$ 200 extra por meta atingida. Incentiva performance e resultados')
    ]
  },
  {
    title: t('Exemplo Completo'),
    icon: 'pi pi-calculator',
    description: t('Quanto recebe um Profissional com múltiplas regras:'),
    tips: [
      t('┌─────────────────────────────────────┐'),
      t('│ Fixo (cargo):        R$ 1.000,00   │'),
      t('│ Comissão (10%):      R$ 500,00    │'),
      t('│ Bonificação (meta):   R$ 200,00    │'),
      t('│─────────────────────────────────────│'),
      t('│ TOTAL A RECEBER:      R$ 1.700,00  │'),
      t('└─────────────────────────────────────┘')
    ]
  },
  {
    title: t('Como Configurar'),
    icon: 'pi pi-pencil',
    description: t('Passo a passo para criar uma regra:'),
    tips: [
      t('Clique em "Nova regra"'),
      t('Dê um nome para a regra (ex: "Profissional 10%")'),
      t('Escolha o tipo: Fixo, Comissão ou Bonificação'),
      t('Defina os valores/percentuais'),
      t('Selecione quais funcionários se aplicam'),
      t('Salve a regra'),
      t('IMPORTANTE: Um funcionário pode ter MÚLTIPLAS regras (soma de todas)'),
      t('Regras desativadas NÃO entram no cálculo'),
      t('⚠️ ATENÇÃO: Uma vez criada, a regra NÃO PODERÁ ser editada'),
      t('Para modificar uma regra existente: DESATIVE a antiga e crie uma NOVA regra'),
      t('Regras afetam APENAS períodos futuros (não retroativo)')
    ]
  }
];
</script>

<style scoped>
.finance-periods-page {
  padding: 2rem;
}

.page-header {
  margin-bottom: 1.5rem;
}

.page-header h1 {
  margin: 0;
  font-size: 2rem;
  font-weight: 600;
}

/* Remove duplicate padding from child pages when inside tabs */
:deep(.payroll-page),
:deep(.payroll-refunds-page),
:deep(.payroll-advances-page),
:deep(.employee-deductions-page),
:deep(.payment-rules-page) {
  padding: 0;
}

/* Hide page titles from child components since we have descriptors, but keep action buttons */
:deep(.payroll-page > .page-header > h1),
:deep(.payment-rules-page > .page-header > h1) {
  display: none;
}

:deep(.payroll-refunds-page > .page-header > .page-title),
:deep(.payroll-advances-page > .page-header > .page-title),
:deep(.employee-deductions-page > .page-header > .page-title),
:deep(.payroll-refunds-page > .page-header > .page-description),
:deep(.payroll-advances-page > .page-header > .page-description),
:deep(.employee-deductions-page > .page-header > .page-description) {
  display: none;
}

/* Keep action bars visible and properly styled */
:deep(.payroll-page > .page-header),
:deep(.payment-rules-page > .page-header) {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 1rem;
}

:deep(.payroll-refunds-page > .page-header),
:deep(.payroll-advances-page > .page-header),
:deep(.employee-deductions-page > .page-header) {
  display: flex;
  flex-direction: row-reverse;
  justify-content: flex-start;
  margin-bottom: 1rem;
}

:deep(.payroll-page > .page-header .header-actions),
:deep(.payment-rules-page > .page-header .header-actions) {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

:deep(.payroll-refunds-page > .page-header .action-bar),
:deep(.payroll-advances-page > .page-header .action-bar),
:deep(.employee-deductions-page > .page-header .action-bar) {
  margin-bottom: 0;
}

/* Adjust spacing after tab descriptor */
:deep(.tab-descriptor) {
  margin-bottom: 1rem;
}

/* Ensure content cards have proper spacing */
:deep(.payroll-page > .p-card),
:deep(.payroll-refunds-page),
:deep(.payroll-advances-page),
:deep(.employee-deductions-page),
:deep(.payment-rules-page > .p-card) {
  margin-top: 0;
}
</style>
